import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { Plus, FileUp } from '@carboledger/ui/icons';
import { ROUTES, MODULE_CERTIFICATE_TYPES } from '@/app/routes';
import { PageHeader } from '@/components/layout';
import { ConfirmDialog } from '@/components/feedback';
import {
  LinkOrdersForShipment,
  LINK_ORDER_FLOWS,
  aggregateLinkRows,
  buildOutgoingShipmentLinkFields,
  DiscrepancyModal,
} from '@/components/common';
import { useTranslations, useAssistantFilters } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useDeleteOutgoingShipmentMutation } from '../api/outgoingShipmentsApi';
import { buildOutgoingShipmentRowDiscrepancySections } from '../constants/outgoingShipmentDiscrepancy';
import { OUTGOING_SHIPMENT_ACTIONS } from '../constants/outgoingShipmentActions';
import styles from './OutgoingShipmentsPage.module.scss';
import { OutgoingShipmentsTable } from '../components/OutgoingShipmentsTable';

// Identity rows the card only shows when every selected shipment agrees on them.
const SHIPMENT_IDENTITY_KEYS = ['soldTo', 'shippingTo'];

// Outbounds ▸ Outgoing shipments.
export const OutgoingShipmentsPage = () => {
  const t = useTranslations();
  const assistantFilters = useAssistantFilters();
  const navigate = useNavigate();
  const { module } = useParams();

  const [deleteRow, setDeleteRow] = useState(null);
  const [linkShipments, setLinkShipments] = useState(EMPTY_LIST);
  const [linkFlow, setLinkFlow] = useState(LINK_ORDER_FLOWS.shipmentToOrders);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const tableRef = useRef(null);
  const [deleteOutgoingShipment] = useDeleteOutgoingShipmentMutation();

  // Ids with a DELETE in flight — gates the double-click RTK Query won't dedupe.
  const deletingIdsRef = useRef(new Set());

  const [discrepancy, setDiscrepancy] = useState(null);
  const discrepancyRow = discrepancy?.row ?? null;
  const handleOpenDiscrepancy = useCallback(
    (row, unitType) => setDiscrepancy({ row, unitType }),
    [],
  );
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancy(null), []);

  const discrepancySections = useMemo(
    () => buildOutgoingShipmentRowDiscrepancySections(t, discrepancyRow, discrepancy?.unitType),
    [t, discrepancyRow, discrepancy],
  );

  const handleBulkLinkOrders = useCallback((selected) => {
    setLinkShipments(selected);
    setLinkFlow(LINK_ORDER_FLOWS.shipmentsToOrder);
    setIsLinkOpen(true);
  }, []);

  const handleCloseLink = useCallback(() => {
    setIsLinkOpen(false);
    setLinkShipments(EMPTY_LIST);
  }, []);

  const handleLinked = useCallback(() => tableRef.current?.clearSelection(), []);

  const bulkActions = useMemo(
    () => [
      {
        id: 'link-orders',
        label: t('OUTGOING_SHIPMENTS_ACTION_LINK_ORDERS'),
        onClick: handleBulkLinkOrders,
      },
    ],
    [t, handleBulkLinkOrders],
  );

  // One shipment or many, the card is the same five rows — Figma 4196:123484 / 4196:122493.
  const linkShipment = useMemo(
    () => aggregateLinkRows(linkShipments, SHIPMENT_IDENTITY_KEYS),
    [linkShipments],
  );

  const linkDetailFields = useCallback(
    (unitType) => buildOutgoingShipmentLinkFields(t, linkShipment, unitType),
    [t, linkShipment],
  );

  const handleOpenShipment = useCallback(
    (row) => {
      navigate(ROUTES.outgoingShipmentDetail(module, row.id));
    },
    [navigate, module],
  );

  const handleAdd = useCallback(() => {
    navigate(ROUTES.outgoingShipmentCreate(module));
  }, [navigate, module]);

  // TODO: the import / row flows land with the Add order screen.
  // The row action passes its own row in, same as handleOpenShipment above.
  const handleFulfill = useCallback(
    (row) => navigate(ROUTES.outgoingShipmentFulfill(module, row.id)),
    [navigate, module],
  );

  // One shipment takes orders directly — no match check, since there is nothing to match against.
  const handleLinkOrders = useCallback((row) => {
    setLinkShipments([row]);
    setLinkFlow(LINK_ORDER_FLOWS.shipmentToOrders);
    setIsLinkOpen(true);
  }, []);

  const handleEdit = useCallback(
    (row) => {
      navigate(ROUTES.outgoingShipmentEdit(module, row.id));
    },
    [navigate, module],
  );

  const handleDelete = useCallback((row) => setDeleteRow(row), []);
  const handleCancelDelete = useCallback(() => setDeleteRow(null), []);

  const handleConfirmDelete = useCallback(async () => {
    const id = deleteRow?.id;
    if (id == null || deletingIdsRef.current.has(id)) return;
    deletingIdsRef.current.add(id);
    try {
      await deleteOutgoingShipment({ module, id }).unwrap();
      setDeleteRow(null);
    } catch {
      // notifyApiErrors already surfaced it.
    } finally {
      deletingIdsRef.current.delete(id);
    }
  }, [deleteOutgoingShipment, module, deleteRow]);

  const addIcon = useMemo(() => <Plus size={16} />, []);
  const importIcon = useMemo(() => <FileUp size={16} />, []);

  const rowActions = useMemo(
    () => [
      {
        id: 'fulfill-shipment',
        label: t('ACTION_FULFILL'),
        variant: ButtonVariants.tertiary,
        onClick: handleFulfill,
      },
    ],
    [t, handleFulfill],
  );

  const rowMenuActions = useMemo(
    () => [
      {
        id: 'link-orders',
        label: t('OUTGOING_SHIPMENTS_ACTION_LINK_ORDERS'),
        onClick: handleLinkOrders,
      },
      { id: 'edit', label: t('COMMON_EDIT'), onClick: handleEdit },
      {
        id: 'delete',
        label: t('COMMON_DELETE'),
        disabled: OUTGOING_SHIPMENT_ACTIONS.delete.disabled,
        onClick: handleDelete,
      },
    ],
    [t, handleLinkOrders, handleEdit, handleDelete],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('OUTGOING_SHIPMENTS_EMPTY_TITLE'),
      description: t('OUTGOING_SHIPMENTS_EMPTY_DESCRIPTION'),
      primaryLabel: t('OUTGOING_SHIPMENTS_ADD'),
      onPrimaryClick: handleAdd,
      primaryIcon: addIcon,
    }),
    [t, handleAdd, addIcon],
  );

  const noResults = useMemo(
    () => ({
      title: t('OUTGOING_SHIPMENTS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const headerActions = useMemo(
    () => (
      <>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          // TODO: enable once the shared .xlsx import wizard exists.
          disabled
        >
          {t('OUTGOING_SHIPMENTS_IMPORT')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          onClick={handleAdd}
        >
          {t('OUTGOING_SHIPMENTS_ADD')}
        </Button>
      </>
    ),
    [t, handleAdd, importIcon, addIcon],
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t('OUTGOING_SHIPMENTS_TITLE')} actions={headerActions} />
      <OutgoingShipmentsTable
        tableRef={tableRef}
        extraFilters={assistantFilters}
        certificateType={MODULE_CERTIFICATE_TYPES[module]}
        emptyState={emptyState}
        noResults={noResults}
        onOpenShipment={handleOpenShipment}
        onOpenDiscrepancy={handleOpenDiscrepancy}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        bulkActions={bulkActions}
      />

      <LinkOrdersForShipment
        module={module}
        flow={linkFlow}
        shipments={linkShipments}
        detailFields={linkDetailFields}
        open={isLinkOpen}
        onClose={handleCloseLink}
        onLinked={handleLinked}
      />

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.shipmentId}
        message={t('OUTGOING_SHIPMENTS_DETAIL_DISCREPANCY_MESSAGE')}
        sections={discrepancySections}
      />

      <ConfirmDialog
        open={deleteRow != null}
        title={t('OUTGOING_SHIPMENTS_DELETE_TITLE')}
        bodyTitle={t('OUTGOING_SHIPMENTS_DELETE_BODY_TITLE', { shipmentId: deleteRow?.shipmentId })}
        description={t('OUTGOING_SHIPMENTS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
